import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { askGemini, buildFinancialContext } from '../../utils/gemini';
import Mawbot from '../shared/Mawbot';

const CHAPTERS = [
  {
    id: 1, icon: 'fa-magnifying-glass-chart', color: '#378ADD',
    title: 'Entendendo seus gastos',
    desc: 'Veja para onde seu dinheiro está indo',
    prompts: [
      {
        id: 1, title: 'Mapa dos gastos do mês',
        desc: 'Tabela completa com categorias e % da renda',
        prompt: `Com base nos meus dados financeiros reais, organize meus gastos em uma tabela com: categoria, valor estimado e porcentagem em relação à minha renda. Depois traga um resumo em linguagem simples explicando como meu dinheiro está sendo distribuído este mês.`,
      },
      {
        id: 2, title: 'Classificando por categorias',
        desc: 'Moradia, Alimentação, Transporte, Lazer e mais',
        prompt: `Com base nos meus gastos reais, organize-os nas seguintes categorias: Moradia, Alimentação, Transporte, Lazer, Saúde, Educação, Contas e serviços, Outros. No final me diga: 1) quais 3 categorias mais consomem meu orçamento; 2) se existe algum desequilíbrio evidente.`,
      },
      {
        id: 3, title: 'Fixos vs Variáveis',
        desc: 'Onde é mais fácil fazer ajustes',
        prompt: `Com base nos meus dados, separe meus gastos em fixos (difíceis de mudar) e variáveis (que podem ser ajustados). Some o total de cada grupo e me diga qual porcentagem da minha renda está em cada um. Explique onde estão as maiores oportunidades de ajuste.`,
      },
    ],
  },
  {
    id: 2, icon: 'fa-scissors', color: '#1d9e75',
    title: 'Onde economizar',
    desc: 'Cortes inteligentes sem perder qualidade de vida',
    prompts: [
      {
        id: 4, title: 'Vazamentos de dinheiro',
        desc: 'Pequenos gastos que se acumulam sem perceber',
        prompt: `Analisando meus gastos reais, identifique possíveis "vazamentos de dinheiro": pequenos gastos recorrentes que somados representam uma fatia relevante do meu orçamento. Me entregue: 1) lista dos 5 principais vazamentos com total mensal; 2) quanto isso representa em % da renda; 3) impacto ao longo de um ano.`,
      },
      {
        id: 5, title: 'Cortes inteligentes',
        desc: 'Até 10 sugestões com impacto no conforto',
        prompt: `Com base na minha realidade financeira real, sugira até 10 cortes ou ajustes para eu guardar mais dinheiro sem piorar muito minha qualidade de vida. Para cada sugestão: qual gasto seria ajustado, quanto economizaria por mês (estimado), e nível de impacto no conforto (baixo, médio ou alto). Destaque as 3 com maior impacto financeiro e menor impacto no conforto.`,
      },
      {
        id: 6, title: '3 planos de ajuste',
        desc: 'Leve, moderado e intenso — você escolhe',
        prompt: `Monte três planos de ajuste de gastos baseados na minha situação real: Plano leve (mudanças quase imperceptíveis), Plano moderado (mudanças viáveis) e Plano intenso (para acelerar a economia). Para cada plano: o que mudaria, quanto economizaria por mês e prós/contras. Me ajude a escolher qual faz mais sentido para meu perfil.`,
      },
    ],
  },
  {
    id: 3, icon: 'fa-bullseye', color: 'var(--gold)',
    title: 'Definindo metas',
    desc: 'Metas claras e realistas para seu dinheiro',
    prompts: [
      {
        id: 7, title: 'Metas de curto, médio e longo prazo',
        desc: 'Estrutura completa com valores e prazos',
        prompt: `Considerando minha situação financeira real, me ajude a estruturar: 2 metas de curto prazo (até 1 ano), 2 de médio prazo (1 a 5 anos) e 2 de longo prazo (acima de 5 anos). Organize em uma tabela com: meta, prazo, valor alvo estimado e importância. Sugira metas típicas para alguém no meu perfil financeiro.`,
      },
      {
        id: 8, title: 'Quanto guardar por meta',
        desc: 'Valor mensal necessário para cada objetivo',
        prompt: `Com base nas minhas metas e na minha renda real, calcule quanto precisaria guardar por mês para atingir cada meta. Me mostre: o valor mensal para cada meta, o total se quisesse perseguir todas ao mesmo tempo, e uma sugestão de priorização considerando o quanto consigo guardar hoje.`,
      },
      {
        id: 9, title: 'Prioridade entre metas',
        desc: 'O que focar agora e o que pode esperar',
        prompt: `Usando minhas metas e situação financeira real, me ajude a priorizar. Considere: urgência, impacto na minha tranquilidade financeira (ex: reserva de emergência) e impacto nos meus objetivos de vida. Sugira uma ordem de prioridade e quais metas devem receber mais dinheiro agora. Explique como se estivesse conversando com um amigo.`,
      },
    ],
  },
  {
    id: 4, icon: 'fa-calendar-check', color: '#9b59b6',
    title: 'Plano mensal',
    desc: 'Transforme intenção em ação concreta',
    prompts: [
      {
        id: 10, title: 'Quanto guardar por mês',
        desc: 'Plano personalizado baseado na sua renda real',
        prompt: `Considerando minha renda, despesas e metas reais, monte um plano simples dizendo: quanto faz sentido eu tentar guardar por mês (em R$ e em % da renda), como dividir esse valor entre as metas prioritárias, e um resumo do meu "plano mensal de guardar dinheiro".`,
      },
      {
        id: 11, title: 'Simulação de cenários',
        desc: 'Conservador, realista e agressivo',
        prompt: `Monte três cenários de quanto eu poderia guardar por mês com base na minha realidade financeira real: Conservador (pouco esforço), Realista (esforço viável) e Agressivo (maior esforço). Para cada: quanto guardaria por mês, em quanto tempo atingiria minha principal meta, e prós/contras. Faça uma recomendação do cenário mais adequado.`,
      },
      {
        id: 12, title: 'Plano de ação em 10 passos',
        desc: 'Passos práticos para começar agora',
        prompt: `Com base na minha situação financeira real, crie um plano de ação em até 10 passos simples para eu começar a guardar mais dinheiro a partir do próximo mês. Seja bem prático (ex: "Passo 1: cancelar assinatura X", "Passo 2: transferência automática de R$ Y"). Inclua dicas de como lembrar de cada passo.`,
      },
    ],
  },
  {
    id: 5, icon: 'fa-rotate', color: '#e67e22',
    title: 'Rotina financeira',
    desc: 'Mantenha o hábito ao longo dos meses',
    prompts: [
      {
        id: 13, title: 'Checklist semanal',
        desc: 'Revisão rápida de 20 minutos por semana',
        prompt: `Crie um checklist semanal simples para eu acompanhar minha vida financeira em menos de 20 minutos por semana, considerando meu perfil de gastos real. Inclua itens como revisar gastos, verificar se estou dentro do planejado, e identificar gastos fora do padrão. Sugira dia e horário ideal para essa revisão.`,
      },
      {
        id: 14, title: 'Rotina mensal de revisão',
        desc: 'O que deu certo, o que ajustar',
        prompt: `Monte uma rotina mensal de revisão financeira personalizada para o meu perfil, com foco em: verificar se guardei o valor planejado, entender o que deu certo e errado, e ajustar o plano para o próximo mês. Inclua um "roteiro de perguntas" que eu possa responder todo mês.`,
      },
      {
        id: 15, title: 'Plano para meses difíceis',
        desc: 'Estratégia para não desistir quando apertar',
        prompt: `Crie um "plano de emergência" personalizado para meses em que meus gastos subirem além do esperado. Detalhe: quais gastos revisar primeiro, como manter o hábito de guardar mesmo que seja pouco, e uma estratégia de "mínimo aceitável". Inclua uma mensagem motivacional sobre por que não faz sentido desistir por causa de um mês ruim.`,
      },
    ],
  },
  {
    id: 6, icon: 'fa-chart-line', color: '#27ae60',
    title: 'Começando a investir',
    desc: 'O próximo passo com o dinheiro que você guarda',
    prompts: [
      {
        id: 16, title: 'Meu perfil de investidor',
        desc: 'Conservador, moderado ou arrojado?',
        prompt: `Com base na minha situação financeira real (renda, dívidas, metas, reservas), me ajude a identificar meu perfil de investidor. Considere: estabilidade da renda, prazos das minhas metas, como me sentiria vendo oscilações. Faça um resumo em linguagem simples do que isso significa na prática para os meus investimentos.`,
      },
      {
        id: 17, title: 'Produtos financeiros básicos',
        desc: 'O que você precisa saber antes de investir',
        prompt: `Considerando meu perfil financeiro real, explique em linguagem simples os principais tipos de investimentos que eu deveria conhecer: reserva de emergência (renda fixa com liquidez), investimentos de médio prazo e longo prazo. Para cada: para que serve, principais riscos, e em quais situações é usado.`,
      },
      {
        id: 18, title: 'Roteiro antes de investir',
        desc: 'Checklist completo para avaliar qualquer investimento',
        prompt: `Crie um roteiro em passos simples para eu estudar qualquer investimento antes de aplicar meu dinheiro, considerando meu perfil e minhas metas reais. Inclua: o que pesquisar, que perguntas responder, que riscos considerar, e como avaliar se combina com minhas metas. Organize como um checklist reutilizável.`,
      },
    ],
  },
  {
    id: 7, icon: 'fa-trophy', color: '#e74c3c',
    title: 'Consistência',
    desc: 'Guardar dinheiro é um jogo de longo prazo',
    prompts: [
      {
        id: 19, title: 'Revisão trimestral das metas',
        desc: 'Ajuste de rota sem culpa',
        prompt: `Monte um roteiro de revisão trimestral das minhas metas financeiras, baseado na minha situação atual. Inclua: o que checar, como saber se estou avançando bem, como decidir se aumento ou diminuo o valor que estou guardando, e como ajustar metas se minha renda mudar. Escreva um resumo sobre a importância de ajustar o plano sem culpa.`,
      },
      {
        id: 20, title: 'Sistema de recompensas',
        desc: 'Motivação para continuar mês após mês',
        prompt: `Me ajude a criar um sistema de motivação para continuar guardando mais dinheiro todo mês, considerando meu progresso real. Sugira: 5 ideias de recompensas simples ao bater metas (mensais ou trimestrais), formas de acompanhar visualmente meu progresso, e uma frase ou mantra pessoal. Organize de forma prática para eu aplicar no dia a dia.`,
      },
    ],
  },
];

// Calcula quantos dias de dados o usuário tem baseado nos lançamentos
function calcDataLevel(state) {
  const txs = state.transactions || [];
  if (txs.length === 0) return { days: 0, level: 0 };

  const dates = txs.map(t => new Date(t.date)).filter(d => !isNaN(d));
  if (dates.length === 0) return { days: 0, level: 0 };

  const oldest = new Date(Math.min(...dates));
  const days = Math.floor((Date.now() - oldest.getTime()) / (1000 * 60 * 60 * 24));

  let level = 0;
  if (days >= 30) level = 3;       // todos os capítulos
  else if (days >= 7) level = 2;   // capítulos 1 e 2
  else if (days >= 1) level = 1;   // bloqueado mas com progresso

  return { days, level, txCount: txs.length };
}

// Qual nível mínimo cada capítulo exige
const CHAPTER_MIN_LEVEL = { 1: 2, 2: 2, 3: 3, 4: 3, 5: 3, 6: 3, 7: 3 };

export default function AIConsultor() {
  const { state } = useApp();
  const [activeChapter, setActiveChapter] = useState(null);
  const [loading, setLoading] = useState(null);
  const [responses, setResponses] = useState({});
  const [error, setError] = useState(null);

  const hasKey = !!process.env.REACT_APP_GEMINI_KEY;
  const { days, level, txCount } = calcDataLevel(state);

  // Progresso para o próximo desbloqueio
  const nextUnlock = level < 2 ? 7 : level < 3 ? 30 : null;
  const progressPct = nextUnlock ? Math.min(100, Math.round((days / nextUnlock) * 100)) : 100;

  const handlePrompt = async (promptId, promptText) => {
    if (loading) return;
    setLoading(promptId);
    setError(null);
    try {
      const context = buildFinancialContext(state);
      const response = await askGemini(promptText, context);
      setResponses(prev => ({ ...prev, [promptId]: response }));
      try { localStorage.setItem('maw_ai_used', '1'); } catch {}
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  const formatResponse = (text) => {
    return text
      .split('\n')
      .map((line, i) => {
        if (line.startsWith('## ')) return <h3 key={i} style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '16px 0 6px' }}>{line.replace('## ', '')}</h3>;
        if (line.startsWith('# ')) return <h2 key={i} style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: '16px 0 6px' }}>{line.replace('# ', '')}</h2>;
        if (line.startsWith('**') && line.endsWith('**')) return <p key={i} style={{ fontWeight: 700, margin: '8px 0 2px', color: 'var(--text)' }}>{line.replace(/\*\*/g, '')}</p>;
        if (line.startsWith('- ') || line.startsWith('• ')) return <div key={i} style={{ display: 'flex', gap: 8, margin: '3px 0', fontSize: 13 }}><span style={{ color: 'var(--gold)', flexShrink: 0 }}>•</span><span style={{ color: 'var(--text2)' }}>{line.replace(/^[-•]\s/, '').replace(/\*\*/g, '')}</span></div>;
        if (/^\d+\.\s/.test(line)) return <div key={i} style={{ display: 'flex', gap: 8, margin: '4px 0', fontSize: 13 }}><span style={{ color: 'var(--gold)', fontWeight: 700, flexShrink: 0 }}>{line.match(/^\d+/)[0]}.</span><span style={{ color: 'var(--text2)' }}>{line.replace(/^\d+\.\s/, '').replace(/\*\*/g, '')}</span></div>;
        if (line.trim() === '') return <div key={i} style={{ height: 6 }} />;
        return <p key={i} style={{ fontSize: 13, color: 'var(--text2)', margin: '3px 0', lineHeight: 1.6 }}>{line.replace(/\*\*/g, '')}</p>;
      });
  };

  // Tela de bloqueio total (menos de 1 dia de dados)
  if (level === 0) {
    return (
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Mawbot size={30} style={{ flexShrink: 0 }} />
            Consultor IA
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
            Análises personalizadas baseadas nos seus dados financeiros reais
          </div>
        </div>
        <div style={{ border: '1px solid var(--border)', borderRadius: 16, padding: '48px 32px', textAlign: 'center', background: 'var(--card)' }}>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}><Mawbot size={88} /></div>
          <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--text)', marginBottom: 8 }}>O Consultor IA está quase pronto para você!</div>
          <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, maxWidth: 480, margin: '0 auto 24px' }}>
            Para gerar análises úteis e personalizadas, preciso de pelo menos <strong style={{ color: 'var(--text)' }}>7 dias de lançamentos</strong> no sistema. Quanto mais dados você tiver, melhores serão as análises!
          </div>
          <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: '20px 24px', maxWidth: 360, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
              <span>Seu progresso</span>
              <span style={{ fontWeight: 700, color: 'var(--text)' }}>0 de 7 dias</span>
            </div>
            <div style={{ height: 10, borderRadius: 99, background: 'var(--border)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: '0%', borderRadius: 99, background: 'var(--gold)' }} />
            </div>
            <div style={{ marginTop: 16, fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>
              <i className="fas fa-arrow-right" style={{ color: 'var(--gold)', marginRight: 8 }}></i>
              Vá em <strong>Lançamentos</strong> e comece a registrar suas receitas e despesas!
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Mawbot size={30} style={{ flexShrink: 0 }} />
          Consultor IA
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
          Análises personalizadas baseadas nos seus dados financeiros reais
        </div>
      </div>

      {/* Barra de progresso de dados */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '14px 18px', marginBottom: 20, background: 'var(--card)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-database" style={{ color: 'var(--gold)', fontSize: 13 }}></i>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              {level >= 3 ? 'Consultor totalmente desbloqueado!' : level === 2 ? 'Capítulos 1 e 2 desbloqueados' : 'Primeiros dados detectados'}
            </span>
          </div>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            {txCount} lançamento{txCount !== 1 ? 's' : ''} · {days} dia{days !== 1 ? 's' : ''} de histórico
          </span>
        </div>
        <div style={{ height: 8, borderRadius: 99, background: 'var(--surface2)', overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 99, transition: 'width 0.5s ease',
            width: `${progressPct}%`,
            background: level >= 3 ? '#1d9e75' : 'linear-gradient(90deg, var(--gold), #f0d060)',
          }} />
        </div>
        {nextUnlock && (
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
            {level === 2
              ? `Faltam ${Math.max(0, 30 - days)} dia${30 - days !== 1 ? 's' : ''} para desbloquear todos os capítulos`
              : `Faltam ${Math.max(0, 7 - days)} dia${7 - days !== 1 ? 's' : ''} para desbloquear os primeiros capítulos`
            }
          </div>
        )}
      </div>

      {/* API key missing warning */}
      {!hasKey && (
        <div style={{ background: '#fff5f0', border: '1px solid #fca5a5', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <i className="fas fa-triangle-exclamation" style={{ color: '#e24b4a', fontSize: 18, marginTop: 2, flexShrink: 0 }}></i>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#991b1b', marginBottom: 4 }}>Chave da API não configurada</div>
            <div style={{ fontSize: 13, color: '#7f1d1d', lineHeight: 1.6 }}>
              Para usar o Consultor IA, adicione sua chave do Gemini no arquivo <code style={{ background: '#fee2e2', padding: '1px 5px', borderRadius: 4 }}>.env</code> na raiz do projeto.
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: '#fff5f0', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#991b1b', display: 'flex', gap: 8 }}>
          <i className="fas fa-circle-exclamation"></i>
          <span>{error}</span>
        </div>
      )}

      {/* Chapters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {CHAPTERS.map(chapter => {
          const minLevel = CHAPTER_MIN_LEVEL[chapter.id] || 2;
          const chapterLocked = level < minLevel;
          const unlockDays = minLevel === 2 ? 7 : 30;

          return (
            <div key={chapter.id} style={{
              border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden',
              background: 'var(--card)', opacity: chapterLocked ? 0.7 : 1,
            }}>
              {/* Chapter header */}
              <button
                onClick={() => !chapterLocked && setActiveChapter(activeChapter === chapter.id ? null : chapter.id)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 14,
                  padding: '16px 20px', background: 'none', border: 'none',
                  cursor: chapterLocked ? 'default' : 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ width: 42, height: 42, borderRadius: 12, background: chapterLocked ? 'var(--surface2)' : `${chapter.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`fas ${chapterLocked ? 'fa-lock' : chapter.icon}`} style={{ color: chapterLocked ? 'var(--muted)' : chapter.color, fontSize: 18 }}></i>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: chapterLocked ? 'var(--muted)' : 'var(--text)' }}>
                    Capítulo {chapter.id} — {chapter.title}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                    {chapterLocked
                      ? `🔒 Disponível com ${unlockDays} dias de dados — você está em ${days} dia${days !== 1 ? 's' : ''}!`
                      : chapter.desc
                    }
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {chapterLocked ? (
                    <span style={{ fontSize: 11, background: 'var(--surface2)', color: 'var(--muted)', borderRadius: 20, padding: '3px 10px', fontWeight: 600 }}>
                      Bloqueado
                    </span>
                  ) : (
                    <>
                      <span style={{ fontSize: 11, background: `${chapter.color}18`, color: chapter.color, borderRadius: 20, padding: '3px 10px', fontWeight: 600 }}>
                        {chapter.prompts.length} análises
                      </span>
                      <i className={`fas fa-chevron-${activeChapter === chapter.id ? 'up' : 'down'}`} style={{ color: 'var(--muted)', fontSize: 12 }}></i>
                    </>
                  )}
                </div>
              </button>

              {/* Prompts list */}
              {!chapterLocked && activeChapter === chapter.id && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {chapter.prompts.map(p => (
                    <div key={p.id} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
                            <span style={{ color: chapter.color, marginRight: 6, fontWeight: 700 }}>#{p.id}</span>
                            {p.title}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{p.desc}</div>
                        </div>
                        <button
                          onClick={() => handlePrompt(p.id, p.prompt)}
                          disabled={loading === p.id || !hasKey}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 7,
                            padding: '8px 16px', borderRadius: 8, border: 'none',
                            cursor: loading === p.id || !hasKey ? 'default' : 'pointer',
                            background: loading === p.id || !hasKey ? 'var(--surface2)' : chapter.color,
                            color: loading === p.id || !hasKey ? 'var(--muted)' : '#fff',
                            fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0,
                            transition: 'opacity 0.15s',
                          }}
                        >
                          {loading === p.id
                            ? <><i className="fas fa-circle-notch fa-spin"></i> Analisando...</>
                            : responses[p.id]
                            ? <><i className="fas fa-rotate-right"></i> Reanalisar</>
                            : <><i className="fas fa-sparkles"></i> Analisar</>
                          }
                        </button>
                      </div>

                      {responses[p.id] && (
                        <div style={{ borderTop: '1px solid var(--border)', padding: '16px 18px', background: 'var(--surface2)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
                            <i className="fas fa-robot" style={{ color: chapter.color, fontSize: 13 }}></i>
                            <span style={{ fontSize: 12, fontWeight: 600, color: chapter.color }}>Análise do Consultor IA</span>
                          </div>
                          <div style={{ lineHeight: 1.6 }}>
                            {formatResponse(responses[p.id])}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--surface2)', borderRadius: 10, fontSize: 11, color: 'var(--muted)', lineHeight: 1.7 }}>
        <i className="fas fa-circle-info" style={{ marginRight: 6 }}></i>
        As análises são geradas por IA com base nos seus dados reais e têm caráter educacional. Sempre confirme informações importantes com um profissional financeiro.
      </div>
    </div>
  );
}
